import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FiUserPlus, FiSearch, FiRefreshCw, FiUsers, FiMail, FiUser, FiHash } from 'react-icons/fi';
import { useToast } from './ToastContext';
import { TailSpin } from 'react-loader-spinner';
import './../Styles/AddEmployeeToOrg.css';

const apiUrl = process.env.REACT_APP_API_URL || '';

const AddEmployeeToOrg = () => {
    // Form fields
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [employeeId, setEmployeeId] = useState('');
    const [emailError, setEmailError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Directory list
    const [orgEmployees, setOrgEmployees] = useState([]);
    const [loadingEmployees, setLoadingEmployees] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const showToast = useToast();

    // Fetch existing organization members with automatic fallback to /api/users
    const fetchOrgEmployees = useCallback(async () => {
        setLoadingEmployees(true);
        try {
            let list = [];
            try {
                const res = await axios.get(`${apiUrl}/api/all-org-employees`);
                if (Array.isArray(res.data) && res.data.length > 0) {
                    list = res.data;
                }
            } catch (e) {
                console.warn('all-org-employees endpoint failed, falling back to /api/users:', e);
            }

            // Fallback to /api/users if all-org-employees was empty or failed
            if (!list || list.length === 0) {
                const usersRes = await axios.get(`${apiUrl}/api/users`);
                const uData = Array.isArray(usersRes.data) ? usersRes.data : [];
                list = uData.map(u => {
                    let roleTitle = 'Employee';
                    if (u.role === 'admin') {
                        roleTitle = u.is_main_manager ? 'Main Manager' : 'Sub-Manager';
                    } else if (u.role === 'lead') {
                        roleTitle = 'Team Lead';
                    } else if (u.role === 'hr') {
                        roleTitle = 'HR';
                    } else if (u.role) {
                        roleTitle = u.role.charAt(0).toUpperCase() + u.role.slice(1);
                    }
                    return {
                        employee_id: u.employee_number || u.employee_id || u._id,
                        name: u.full_name || u.name || u.username || 'N/A',
                        email: u.username || u.email || 'N/A',
                        role: roleTitle
                    };
                });
            }

            setOrgEmployees(list);
        } catch (err) {
            console.error('Error fetching org employees:', err);
        } finally {
            setLoadingEmployees(false);
        }
    }, []);

    useEffect(() => {
        fetchOrgEmployees();
    }, [fetchOrgEmployees]);

    // Validate email format and company domain
    const validateEmail = (value) => {
        if (!value) {
            setEmailError('');
            return;
        }
        const isValidFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        if (isValidFormat && !value.toLowerCase().endsWith('@tsqsinc.com')) {
            setEmailError('Only @tsqsinc.com email addresses are allowed.');
        } else {
            setEmailError('');
        }
    };

    const handleEmailChange = (e) => {
        const val = e.target.value;
        setEmail(val);
        validateEmail(val);
    };

    // Form fields reset
    const handleResetForm = (e) => {
        if (e) {
            e.preventDefault();
        }
        setEmail('');
        setName('');
        setEmployeeId('');
        setEmailError('');
    };

    // Directory search & filter reset
    const handleResetDirectory = () => {
        setSearchTerm('');
        fetchOrgEmployees();
    };

    // Form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        const trimmedEmail = email.trim().toLowerCase();
        const trimmedName = name.trim();
        const trimmedEmpId = employeeId.trim();

        if (!trimmedEmail || !trimmedName || !trimmedEmpId) {
            showToast('Please fill in Email, Name, and Employee ID.', 'warning');
            return;
        }

        if (!trimmedEmail.endsWith('@tsqsinc.com')) {
            setEmailError('Only @tsqsinc.com email addresses are allowed.');
            return;
        }

        setIsSubmitting(true);
        try {
            const res = await axios.post(`${apiUrl}/api/add-to-org`, {
                email: trimmedEmail,
                name: trimmedName,
                employeeId: trimmedEmpId
            });

            if (res.status === 200 || res.status === 201) {
                showToast(`Employee ${trimmedName} (${trimmedEmpId}) added to organization successfully!`, 'success');
                setEmail('');
                setName('');
                setEmployeeId('');
                setEmailError('');
                fetchOrgEmployees();
            } else {
                showToast(res.data?.message || 'Failed to add employee.', 'error');
            }
        } catch (err) {
            console.error('Error adding employee to organization:', err);
            const errMsg = err.response?.data?.error || err.response?.data?.message || 'Failed to add employee to organization.';
            showToast(errMsg, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Filter directory list by search term
    const filteredEmployees = orgEmployees.filter(emp => {
        if (!searchTerm || !searchTerm.trim()) return true;
        const term = searchTerm.trim().toLowerCase();
        const eId = String(emp.employee_id || '').toLowerCase();
        const eName = String(emp.name || '').toLowerCase();
        const eEmail = String(emp.email || '').toLowerCase();
        const eRole = String(emp.role || '').toLowerCase();
        return eId.includes(term) || eName.includes(term) || eEmail.includes(term) || eRole.includes(term);
    });

    return (
        <div className="add-emp-org-container">
            {/* Page Header */}
            <div className="add-emp-org-header">
                <div>
                    <h1 className="add-emp-org-title">Add Employee to Organization</h1>
                </div>
            </div>

            {/* Form Card */}
            <div className="add-emp-form-card">
                <div className="form-card-header">
                    <div className="form-card-icon">
                        <FiUserPlus size={20} />
                    </div>
                    <div>
                        <h2 className="form-card-title">Employee Onboarding Form</h2>
                    </div>
                </div>

                <form onSubmit={handleSubmit} onReset={handleResetForm} className="add-emp-form">
                    <div className="form-grid">
                        {/* Email Input */}
                        <div className="form-field">
                            <label className="field-label">
                                Email Address <span className="req-star">*</span>
                            </label>
                            <div className="input-with-icon">
                                <FiMail className="input-icon" />
                                <input
                                    type="email"
                                    className={`field-input ${emailError ? 'input-error' : ''}`}
                                    placeholder="e.g. employee@tsqsinc.com"
                                    value={email}
                                    onChange={handleEmailChange}
                                    required
                                />
                            </div>
                            {emailError && (
                                <span className="field-error-msg">{emailError}</span>
                            )}
                        </div>

                        {/* Full Name Input */}
                        <div className="form-field">
                            <label className="field-label">
                                Employee Full Name <span className="req-star">*</span>
                            </label>
                            <div className="input-with-icon">
                                <FiUser className="input-icon" />
                                <input
                                    type="text"
                                    className="field-input"
                                    placeholder="e.g. John Doe"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        {/* Employee ID Input */}
                        <div className="form-field">
                            <label className="field-label">
                                Employee ID <span className="req-star">*</span>
                            </label>
                            <div className="input-with-icon">
                                <FiHash className="input-icon" />
                                <input
                                    type="text"
                                    className="field-input"
                                    placeholder="e.g. 10045"
                                    value={employeeId}
                                    onChange={(e) => setEmployeeId(e.target.value)}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn-clear"
                            onClick={handleResetForm}
                            disabled={isSubmitting}
                            title="Reset all form fields"
                        >
                            Reset
                        </button>

                        <button
                            type="submit"
                            className="btn-submit"
                            disabled={isSubmitting || Boolean(emailError)}
                        >
                            {isSubmitting ? (
                                <>
                                    <TailSpin height="16" width="16" color="#ffffff" />
                                    <span>Adding Employee...</span>
                                </>
                            ) : (
                                <>
                                    <FiUserPlus size={16} />
                                    <span>Add to Organization</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {/* Organization Directory Preview Table */}
            <div className="org-directory-card">
                <div className="directory-card-header">
                    <div className="directory-header-left">
                        <div className="directory-icon">
                            <FiUsers size={18} />
                        </div>
                        <div>
                            <h2 className="directory-title">Organization Directory</h2>
                            <span className="directory-count-badge">
                                {orgEmployees.length} Total Registered Members
                            </span>
                        </div>
                    </div>

                    <div className="directory-header-right">
                        {/* Search Box */}
                        <div className="directory-search-box">
                            <FiSearch className="search-icon" />
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Search by name, ID, or email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button
                                    type="button"
                                    className="search-clear-btn"
                                    onClick={() => setSearchTerm('')}
                                    title="Clear search"
                                    aria-label="Clear search"
                                >
                                    ✕
                                </button>
                            )}
                        </div>

                        {/* Reset / Refresh Button */}
                        <button
                            type="button"
                            className={`btn-refresh ${searchTerm ? 'has-filter' : ''}`}
                            onClick={handleResetDirectory}
                            title={searchTerm ? "Reset search filter" : "Refresh directory"}
                            aria-label="Reset search filter and refresh directory"
                        >
                            <FiRefreshCw size={15} className={loadingEmployees ? 'spin' : ''} />
                        </button>
                    </div>
                </div>

                <div className="directory-table-wrapper">
                    {loadingEmployees ? (
                        <div className="directory-loading">
                            <TailSpin height="36" width="36" color="var(--brand, #F59E0B)" />
                            <span>Loading organization directory...</span>
                        </div>
                    ) : filteredEmployees.length === 0 ? (
                        <div className="directory-empty">
                            <p>{searchTerm ? 'No organization members match your search.' : 'No members found in organization.'}</p>
                            {searchTerm && (
                                <button
                                    type="button"
                                    className="btn-clear"
                                    onClick={handleResetDirectory}
                                    style={{ marginTop: '10px' }}
                                >
                                    Reset Search Filter
                                </button>
                            )}
                        </div>
                    ) : (
                        <table className="directory-table">
                            <thead>
                                <tr>
                                    <th>Employee ID</th>
                                    <th>Full Name</th>
                                    <th>Email</th>
                                    <th>Role</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredEmployees.map((emp, idx) => (
                                    <tr key={emp.employee_id || idx}>
                                        <td className="emp-id-cell">
                                            <strong>{emp.employee_id}</strong>
                                        </td>
                                        <td className="emp-name-cell">{emp.name || 'N/A'}</td>
                                        <td className="emp-email-cell">{emp.email || 'N/A'}</td>
                                        <td>
                                            <span className={`role-badge ${String(emp.role || '').toLowerCase().replace(/\s+/g, '-')}`}>
                                                {emp.role || 'Member'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddEmployeeToOrg;
